import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { toast } from "@volca/ui/lib/toast";
import { extractErrorMessage } from "@/core/api/errors";
import { ApiError } from "@/core/api/types";
import { useCreatePayrollSheetApprovalAction } from "@/payroll/pay-periods/query";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import type { ApprovalActionTarget, ApprovalChangeHandler } from "./types";
import { APPROVAL_INTENT, resolveApprovalIntent } from "./utils";

interface UseSheetApprovalActionsParams {
  businessId?: string | null;
  periodId?: string | null;
  runId?: string | null;
  approvalStatus?: IPayrollSheetApprovalStatus | null;
}

const APPROVAL_ERROR_TITLES: Partial<Record<number, string>> = {
  403: "You no longer have permission to approve this sheet",
  404: "This payroll result is no longer available",
  409: "Payroll has been rerun",
  422: "This approval could not be updated",
};

/**
 * Owns the complete interaction around changing a sheet approval: mutation
 * state, success/error feedback, and the confirmation required before removing
 * an existing sign-off.
 */
export const useSheetApprovalActions = ({
  businessId,
  periodId,
  runId,
  approvalStatus,
}: UseSheetApprovalActionsParams) => {
  const [removalRequest, setRemovalRequest] = useState<{
    target: ApprovalActionTarget;
    context: string;
  } | null>(null);
  const { mutate, isPending, variables } = useCreatePayrollSheetApprovalAction(
    businessId,
    periodId,
  );

  const context = `${businessId}/${periodId}/${runId}`;
  // Mutation callbacks stop after unmount. Guard run changes while still mounted.
  const currentContext = useRef(context);
  useLayoutEffect(() => {
    currentContext.current = context;
  }, [context]);
  const submitting = useRef(false);

  const submit = useCallback(
    (
      { accessKey, approvalType, sheet }: ApprovalActionTarget,
      enabled: boolean,
      reason?: string,
    ) => {
      if (!businessId || !periodId || !runId || submitting.current) return;
      if (!accessKey.approval_authorized) return;
      if (approvalStatus !== undefined) {
        const currentGroup = approvalStatus?.access_keys.find(
          (key) => key.id === accessKey.id,
        );
        if (
          !currentGroup?.approval_authorized ||
          (sheet &&
            !currentGroup.sheets?.some((entry) => entry.id === sheet.id))
        ) {
          toast.error(
            "Approval access or sheets have changed. Open the current sheet and try again.",
          );
          return;
        }
      }
      submitting.current = true;
      const submittedContext = context;

      mutate(
        {
          access_key_id: accessKey.id,
          ...(sheet ? { sheet_id: sheet.id } : { scope: "group" as const }),
          originating_ai_script_run_id: runId,
          approval_type: approvalType,
          enabled,
          ...(reason ? { reason } : {}),
        },
        {
          onSuccess: () => {
            if (currentContext.current !== submittedContext) return;
            setRemovalRequest(null);
            toast.success(enabled ? "Approval added" : "Approval removed", {
              description: sheet?.name ?? accessKey.name,
            });
          },
          onError: (error) => {
            if (currentContext.current !== submittedContext) return;
            const { status, message } =
              error instanceof ApiError ? error : extractErrorMessage(error);
            const title =
              APPROVAL_ERROR_TITLES[status ?? 0] ?? "Failed to update approval";

            toast.error(title, { description: message });
          },
          onSettled: () => {
            submitting.current = false;
          },
        },
      );
    },
    [approvalStatus, businessId, context, mutate, periodId, runId],
  );

  const handleChange = useCallback<ApprovalChangeHandler>(
    (accessKey, approvalType, enabled, sheet) => {
      if (submitting.current) return;
      const target = { accessKey, approvalType, ...(sheet ? { sheet } : {}) };
      if (resolveApprovalIntent(enabled) === APPROVAL_INTENT.approve) {
        submit(target, true);
        return;
      }

      setRemovalRequest({ target, context });
    },
    [context, submit],
  );

  const confirmRemoval = useCallback(
    (reason?: string) => {
      if (!removalRequest) return;
      if (removalRequest.context !== context) {
        toast.error(
          "Payroll has changed. Open the current sheet and try again.",
        );
        return;
      }
      submit(removalRequest.target, false, reason);
    },
    [context, removalRequest, submit],
  );

  const pendingApproval =
    isPending && variables
      ? {
          accessKeyId: variables.access_key_id,
          sheetId: variables.sheet_id,
          approvalType: variables.approval_type,
        }
      : null;

  return {
    confirmRemoval,
    handleChange,
    isPending,
    pendingApproval,
    removalTarget: removalRequest?.target ?? null,
    setRemovalOpen: (open: boolean) => {
      if (!open) setRemovalRequest(null);
    },
  };
};
